import * as React from "react";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { HomePromptDTO, HomePromptOption } from "@volley/data";

import {
    isAssessmentLaunchOption,
    isAptaConnectOption,
    isDuprConnectOption,
    isSeeStatsOption,
} from "./constants";

const GREEN = "#3CE97C";

/** Max free-text length captured from the open field, to keep events bounded. */
const ASK_MAX_LENGTH = 500;

interface AskVolleyWidgetProps {
    prompt: HomePromptDTO;
    /** Open the chat overlay. Passing an option value plays that answer on open. */
    onOpen: (optionValue?: string) => void;
    /** Submit free text from the open field; opens the chat seeded with it. */
    onAskAnything: (text: string) => void;
    /** Soft-dismiss the prompt via a dismiss option (e.g. "Maybe later"). */
    onDismiss: (option: HomePromptOption) => void;
    /** Drive the assessment flow (start/resume) without opening the chat. */
    onLaunchAssessment: (option: HomePromptOption) => void;
    /** Open the DUPR connect flow (profile prompt) without opening the chat. */
    onConnectDupr: (option: HomePromptOption) => void;
    /** Open the APTA-id dialog (platform tennis's rating link) instead of content. */
    onConnectApta: (option: HomePromptOption) => void;
    /** Navigate to the performance dashboard ("see my stats") without chat. */
    onSeeStats: (option: HomePromptOption) => void;
    /** Lit treatment during the first-run spotlight (brighter ring + glow). */
    spotlit?: boolean;
}

/**
 * Inline "Ask Volley" widget shown at the top of the home screen. Displays the
 * personalized prompt and its answer options as tappable chips; tapping the
 * card (or a chip) opens the full chat overlay.
 */
export default function AskVolleyWidget({
    prompt,
    onOpen,
    onAskAnything,
    onDismiss,
    onLaunchAssessment,
    onConnectDupr,
    onConnectApta,
    onSeeStats,
    spotlit = false,
}: AskVolleyWidgetProps): React.JSX.Element {
    const [draft, setDraft] = React.useState("");

    const submitDraft = React.useCallback(() => {
        const text = draft.trim();
        if (!text) return;
        onAskAnything(text);
        setDraft("");
    }, [draft, onAskAnything]);

    const canSend = draft.trim().length > 0;

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => onOpen()}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
            sx={{
                position: "relative",
                mt: 1.75,
                borderRadius: 2,
                p: "14px 14px 12px",
                background: `linear-gradient(180deg, rgba(60,233,124,0.04) 0%, rgba(60,233,124,0) 60%), #14213F`,
                border: `1px solid ${spotlit ? "rgba(60,233,124,0.8)" : "rgba(60,233,124,0.35)"}`,
                boxShadow: spotlit
                    ? `0 0 0 3px rgba(60,233,124,0.35), 0 8px 22px rgba(0,0,0,0.35), 0 0 36px rgba(60,233,124,0.25)`
                    : `0 8px 22px rgba(0,0,0,0.35), 0 0 24px rgba(60,233,124,0.06)`,
                color: "#fff",
                cursor: "pointer",
                overflow: "hidden",
                transition: "box-shadow 240ms ease, border-color 240ms ease",
                "&:hover": { borderColor: "rgba(60,233,124,0.5)" },
            }}
        >
            <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 1.25, mb: 1 }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#0B1730",
                        background:
                            "radial-gradient(circle at 30% 30%, #58F08C 0%, #1FA557 60%, #0E6E3A 100%)",
                        boxShadow:
                            "0 0 0 2px rgba(60,233,124,0.4), 0 0 14px rgba(60,233,124,0.45)",
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 900,
                            fontSize: 13,
                            letterSpacing: "0.06em",
                            color: GREEN,
                            textTransform: "uppercase",
                            lineHeight: 1,
                        }}
                    >
                        Ask Volley
                    </Typography>
                    <Stack
                        direction="row"
                        sx={{ alignItems: "center", gap: 0.75, mt: "3px" }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: GREEN,
                                boxShadow: `0 0 6px ${GREEN}`,
                            }}
                        />
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: 11,
                                color: "#94A3B8",
                            }}
                        >
                            {prompt.status?.trim() || "Picked for you"}
                        </Typography>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        ml: "auto",
                        color: "rgba(255,255,255,0.45)",
                        display: "inline-flex",
                    }}
                >
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                </Box>
            </Stack>

            <Typography
                sx={{
                    fontSize: 15,
                    lineHeight: 1.4,
                    fontWeight: 400,
                    mb: 1.5,
                    color: "#fff",
                }}
            >
                {prompt.promptText}
            </Typography>

            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                {prompt.options.map((option) => (
                    <Box
                        key={option.value}
                        component="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isAssessmentLaunchOption(option))
                                onLaunchAssessment(option);
                            else if (isDuprConnectOption(option))
                                onConnectDupr(option);
                            else if (isAptaConnectOption(option))
                                onConnectApta(option);
                            else if (isSeeStatsOption(option))
                                onSeeStats(option);
                            else if (option.dismiss) onDismiss(option);
                            else onOpen(option.value);
                        }}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 1,
                            borderRadius: 999,
                            bgcolor: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(168,184,217,0.22)",
                            // Dismiss chips read as a quieter, secondary action.
                            color: option.dismiss ? "#94A3B8" : "#E2E8F0",
                            fontFamily: "inherit",
                            fontWeight: 500,
                            fontSize: 12.5,
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            transition:
                                "background 120ms ease, border-color 120ms ease",
                            "&:hover": option.dismiss
                                ? { borderColor: "rgba(168,184,217,0.4)" }
                                : {
                                      bgcolor: "rgba(60,233,124,0.06)",
                                      borderColor: "rgba(60,233,124,0.35)",
                                  },
                        }}
                    >
                        {option.label}
                    </Box>
                ))}
            </Stack>

            {/* Open "Ask anything" field — the captured free-text entry point.
                Persistent on the widget so the user can type their own request
                instead of (or in addition to) the suggested options. Clicks and
                keystrokes are kept from bubbling to the card's open handler. */}
            <Stack
                direction="row"
                onClick={(e) => e.stopPropagation()}
                sx={{
                    alignItems: "center",
                    gap: 1,
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: "1px solid rgba(168,184,217,0.12)",
                }}
            >
                <InputBase
                    fullWidth
                    value={draft}
                    placeholder="Ask Volley anything…"
                    inputProps={{
                        maxLength: ASK_MAX_LENGTH,
                        "aria-label": "Ask Volley anything",
                    }}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        // Keep keystrokes from reaching the card's key handler,
                        // which would otherwise swallow Space and open on Enter.
                        e.stopPropagation();
                        if (e.key === "Enter") {
                            e.preventDefault();
                            submitDraft();
                        }
                    }}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        height: 46,
                        borderRadius: "24px",
                        px: 2,
                        color: "#fff",
                        fontSize: 14,
                        // Standing green outline + faint wash so the field reads
                        // as the desired action even after the spotlight is gone.
                        bgcolor: "rgba(60,233,124,0.06)",
                        border: "1.5px solid rgba(60,233,124,0.55)",
                        boxShadow: "0 0 0 3px rgba(60,233,124,0.07)",
                        transition:
                            "border-color 120ms ease, box-shadow 120ms ease, background 120ms ease",
                        "& input::placeholder": {
                            color: "#9DB0D0",
                            opacity: 1,
                        },
                        "&.Mui-focused": {
                            borderColor: GREEN,
                            bgcolor: "rgba(60,233,124,0.1)",
                            boxShadow: "0 0 0 4px rgba(60,233,124,0.18)",
                        },
                    }}
                />
                <Box
                    component="button"
                    type="button"
                    aria-label="send"
                    disabled={!canSend}
                    onClick={(e) => {
                        e.stopPropagation();
                        submitDraft();
                    }}
                    sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: 999,
                        border: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#0B1730",
                        // Full Volley green at rest — the arrow is the action
                        // color, always; just softer while the field is empty.
                        bgcolor: canSend ? GREEN : "rgba(60,233,124,0.82)",
                        boxShadow: canSend
                            ? "0 0 14px rgba(60,233,124,0.35)"
                            : "0 0 10px rgba(60,233,124,0.2)",
                        cursor: canSend ? "pointer" : "default",
                        transition:
                            "background 120ms ease, box-shadow 120ms ease",
                        "&:hover": canSend ? { bgcolor: "#5BF295" } : {},
                        "&:active": canSend ? { bgcolor: "#2FCB69" } : {},
                        "@media (prefers-reduced-motion: no-preference)":
                            canSend
                                ? {
                                      animation:
                                          "askVolleySendPulse 1.8s ease-in-out infinite",
                                      "@keyframes askVolleySendPulse": {
                                          "0%, 100%": {
                                              boxShadow:
                                                  "0 0 14px rgba(60,233,124,0.35)",
                                          },
                                          "50%": {
                                              boxShadow:
                                                  "0 0 22px rgba(60,233,124,0.62)",
                                          },
                                      },
                                  }
                                : {},
                    }}
                >
                    <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                </Box>
            </Stack>
        </Box>
    );
}
